import {
  ArrowCounterClockwise,
  ArrowLeft,
  ArrowRight,
  Database,
  SmileyXEyes,
} from 'phosphor-react';
import { Button, Text } from '@typedream/ui';
import { useEventPlateId } from '@udecode/plate-core';
import { useGetCollectionQuery, useGetPageQuery } from '@/store';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { CMSCollection, errToString } from '@typedream/data';
import LoadingOnComponent from '@/components/LoadingOnComponent';
import { useState } from 'react';
import { useTPlateEditorRef } from '../../plate';
import { useSelectedElement } from '../../store';
import { IOptionsElement } from '../../types';
import { ColumnOptions } from './ColumnOptions';

export function MapFields({
  goBack,
  onClose,
  onBackToConnect,
}: {
  goBack: () => void;
  onClose: () => void;
  onBackToConnect: () => void;
}) {
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const editorID = useEventPlateId();
  const editor = useTPlateEditorRef(editorID as string);
  const { data: page } = useGetPageQuery([spaceID, pageID]);
  const element = useSelectedElement() as IOptionsElement;
  const collectionDataID =
    element?.type === 'collection_cms' ? element.collection_data_id : undefined;
  const pageCollectionData = page?.customization?.collection_data;
  const CMSID = pageCollectionData?.[collectionDataID]?.tableID;

  const schema = element?.type === 'collection_cms' ? element.collection_template?.schema : {};
  const [newSchema, setnewSchema] = useState<Record<string, string>>(schema);

  const { data: collection, isLoading, error } = useGetCollectionQuery([spaceID, CMSID]);

  if (element?.type !== 'collection_cms') {
    return (
      <div className="flex flex-col gap-2 w-full h-full items-center justify-center">
        <div className="p-2 rounded-lg border border-solid border-neutral-200 dark:border-neutral-800">
          <Text variant="error" className="leading-none" as="div">
            <SmileyXEyes size={28} />
          </Text>
        </div>
        <Text variant="main" size="lg" weight="medium">
          Hmm, something went wrong
        </Text>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowCounterClockwise size={16} />}
          onClick={onClose}
        >
          Let&apos;s start over
        </Button>
      </div>
    );
  }

  if (!collection || isLoading) {
    return (
      <div className="flex flex-col gap-2 w-full h-full items-center justify-center">
        <LoadingOnComponent loading={isLoading} />
        {!isLoading && (
          <>
            <div className="p-2 rounded-lg border border-solid border-neutral-200 dark:border-neutral-800">
              <Text variant="error" className="leading-none" as="div">
                <SmileyXEyes size={28} />
              </Text>
            </div>
            <Text variant="main" size="lg" weight="medium">
              Hmm, something went wrong
            </Text>
            {error && (
              <Text variant="secondary" size="xs" weight="medium">
                {!CMSID ? "Looks like the Collection isn't connected properly" : errToString(error)}
              </Text>
            )}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowCounterClockwise size={16} />}
              onClick={onBackToConnect}
            >
              Let&apos;s connect a different collection
            </Button>
          </>
        )}
      </div>
    );
  }

  if (collection?.type !== 'cms') {
    return (
      <div className="flex flex-col gap-2 w-full h-full items-center justify-center">
        <div className="p-2 rounded-lg border border-solid border-neutral-200 dark:border-neutral-800">
          <Text variant="error" className="leading-none" as="div">
            <SmileyXEyes size={28} />
          </Text>
        </div>
        <Text variant="main" size="lg" weight="medium">
          {collection?.type} is not yet supported
        </Text>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowCounterClockwise size={16} />}
          onClick={onBackToConnect}
        >
          Let&apos;s connect a different collection
        </Button>
      </div>
    );
  }

  const cmsCollection = new CMSCollection(collection);

  const onConnectData = async () => {
    const newTemplate = {
      ...element.collection_template,
      schema: newSchema,
    };

    editor.emitter.emit('popup:on-cms-set-template', newTemplate);
    onClose();
  };

  return (
    <div className="flex flex-col gap-2 w-full h-full">
      <div className="flex flex-row gap-4 w-full mb-4 items-center">
        <Button variant="outline" size="xs" onClick={goBack} className="w-fit p-2.5">
          <ArrowLeft size={16} />
        </Button>
        <Text variant="main" size="lg" weight="medium">
          Map your Data
        </Text>
      </div>

      <div className="flex w-full gap-2 grow">
        <div className="flex flex-col gap-2 grow">
          {Object.keys(schema).map((key) => (
            <div className="flex gap-4 items-center">
              <div
                key={key}
                className="bg-neutral-50 dark:bg-neutral-900 rounded-lg px-4 py-2 w-40 gap-2.5 flex items-center"
              >
                <Text variant="secondary" size="xs" weight="medium" className="leading-none">
                  <Database size={16} />
                </Text>
                <Text variant="secondary" size="xs" weight="medium">
                  {key}
                </Text>
              </div>
              <ArrowRight size={16} />
              <ColumnOptions
                collection={cmsCollection}
                value={newSchema[key]}
                setvalue={(val) => setnewSchema({ ...newSchema, [key]: val })}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 pb-48">Preview</div>
      </div>
      <div className="flex justify-end items-center w-full">
        <Button onClick={onConnectData}>Connect Data</Button>
      </div>
    </div>
  );
}
